import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CreditCard, CheckCircle, ArrowRight, Shield, Lightning,
  Sparkle, Globe, Warning, ArrowLeft, Crown,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const PLAN_FEATURES = [
  "Unlimited AI match scoring",
  "Full application draft generation",
  "5 premium marketplaces (LinkedIn, Indeed, Glassdoor, Google Jobs, Adzuna)",
  "Priority job discovery pipeline",
  "AI skill gap analysis",
  "Email job digest",
  "Draft version history",
];

function PlanCard({ plan, planKey, onSelect, loading, selected }) {
  const isIndia = planKey === "india_monthly";
  return (
    <div
      className={`border-2 p-6 cursor-pointer transition-all duration-200 ${
        selected === planKey
          ? "border-brand bg-brand/5 shadow-lg"
          : "border-slate-200 bg-white hover:border-brand/40"
      }`}
      onClick={() => onSelect(planKey)}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <span className="tick-label">{isIndia ? "India" : "Global"}</span>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="font-mono font-black text-4xl text-ink">
              {plan.amount_display}
            </span>
            <span className="text-xs text-ink-soft">/month</span>
          </div>
        </div>
        <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-1 ${
          selected === planKey ? "border-brand bg-brand" : "border-slate-300"
        }`}>
          {selected === planKey && (
            <CheckCircle size={16} weight="fill" className="text-white -m-px" />
          )}
        </div>
      </div>

      <p className="text-sm text-ink-soft leading-relaxed mb-4">{plan.description}</p>

      <div className="text-xs text-ink-soft font-mono">
        {plan.currency} · billed monthly
      </div>

      {isIndia && (
        <Badge className="mt-3 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] uppercase tracking-wider">
          Best for India
        </Badge>
      )}
    </div>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const nav = useNavigate();

  const [plans, setPlans] = useState({});
  const [razorpayKeyId, setRazorpayKeyId] = useState("");
  const [configured, setConfigured] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("india_monthly");

  useEffect(() => {
    loadBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBillingData = async () => {
    setLoading(true);
    try {
      const [plansRes, statusRes] = await Promise.allSettled([
        api.get("/payments/plans"),
        api.get("/payments/status"),
      ]);
      if (plansRes.status === "fulfilled") {
        const d = plansRes.value.data;
        setPlans(d.plans || {});
        setRazorpayKeyId(d.razorpay_key_id || "");
        setConfigured(d.configured || false);
      }
      if (statusRes.status === "fulfilled") {
        setSubStatus(statusRes.value.data);
      }
    } catch (e) {
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const handleCheckout = async () => {
    if (!configured) {
      toast.error("Payment gateway not configured. Add Razorpay keys to the backend .env.");
      return;
    }

    setCheckoutLoading(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error("Could not load payment gateway. Check your connection."); return; }

      const { data: order } = await api.post("/payments/order", { plan: selectedPlan });

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Opplio",
        description: order.plan_name,
        order_id: order.order_id,
        handler: async (response) => {
          try {
            const { data } = await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: selectedPlan,
            });
            toast.success(data.message || "Payment successful! Pro access activated.");
            await loadBillingData();
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment verification failed.");
          }
        },
        prefill: {
          email: user?.email || "",
          name: user?.full_name || "",
        },
        theme: { color: "#002FA7" },
        modal: { backdropclose: false },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed. Try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel your subscription? You keep access until the period ends.")) return;
    try {
      await api.post("/payments/cancel");
      toast.success("Subscription cancelled. Access remains until expiry.");
      await loadBillingData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancellation failed.");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12">

        {/* Back link */}
        <Link to="/home" className="inline-flex items-center gap-2 text-xs text-ink-soft hover:text-brand uppercase tracking-wider font-semibold mb-8">
          <ArrowLeft size={12} weight="bold" /> Home
        </Link>

        {/* Header */}
        <div className="mb-10">
          <span className="tick-label">Billing & Subscription</span>
          <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
            Unlock Pro access.
          </h1>
          <p className="mt-3 text-ink-soft max-w-xl">
            One flat monthly price. No per-draft fees. No usage limits. Cancel any time.
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-10">

          {/* ── Left: Plans or Current Status ── */}
          <div className="xl:col-span-7">

            {/* Active subscription */}
            {!loading && subStatus?.active && (
              <div className="mb-6 border-2 border-emerald-300 bg-emerald-50/60 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Crown size={20} weight="fill" className="text-emerald-600" />
                  <span className="font-display font-bold text-lg">Pro Active</span>
                  <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] uppercase tracking-wider">Active</Badge>
                </div>
                <p className="text-sm text-ink-soft">
                  Plan: <strong className="text-ink">{subStatus.plan_name}</strong> · {subStatus.amount_display}/month
                </p>
                <p className="text-sm text-ink-soft mt-1">
                  Access until: <strong className="text-ink">
                    {subStatus.expires_at ? new Date(subStatus.expires_at).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" }) : "—"}
                  </strong>
                </p>
                {subStatus.razorpay_payment_id && (
                  <p className="mt-1 text-[11px] font-mono text-ink-mute">
                    Payment ID: {subStatus.razorpay_payment_id}
                  </p>
                )}
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="mt-4 rounded-xl border-red-200 text-red-600 hover:bg-red-500 hover:text-white uppercase tracking-wider text-xs h-9 px-4"
                >
                  Cancel subscription
                </Button>
              </div>
            )}

            {/* Gateway not configured warning */}
            {!loading && !configured && (
              <div className="mb-6 border border-amber-300 bg-amber-50 p-4">
                <div className="flex items-start gap-2">
                  <Warning size={16} weight="fill" className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <strong>Payment gateway not configured.</strong> Add your Razorpay test keys to{" "}
                    <code className="font-mono bg-amber-100 px-1">backend/.env</code>:<br />
                    <code className="font-mono text-xs mt-1 block">RAZORPAY_KEY_ID=rzp_test_...</code>
                    <code className="font-mono text-xs">RAZORPAY_KEY_SECRET=your_secret</code>
                    <br />
                    <a
                      href="https://dashboard.razorpay.com/app/keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline mt-1 inline-block"
                    >
                      Get keys from Razorpay dashboard →
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Plan selection */}
            {(!subStatus?.active) && (
              <>
                <h2 className="font-display font-bold text-xl tracking-tight mb-4">Choose your plan</h2>
                {loading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[0, 1].map((i) => (
                      <div key={i} className="border border-slate-200 h-52 animate-pulse bg-slate-50" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {Object.entries(plans)
                      .filter(([key]) => key !== "global_monthly")
                      .map(([key, plan]) => (
                      <PlanCard
                        key={key}
                        planKey={key}
                        plan={plan}
                        selected={selectedPlan}
                        onSelect={setSelectedPlan}
                        loading={checkoutLoading}
                      />
                    ))}
                  </div>
                )}

                {/* Checkout button */}
                <div className="mt-6">
                  <Button
                    onClick={handleCheckout}
                    disabled={checkoutLoading || loading || !configured}
                    className="w-full sm:w-auto rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs h-12 px-8 disabled:opacity-50"
                  >
                    {checkoutLoading ? (
                      "Opening checkout…"
                    ) : (
                      <>
                        <CreditCard size={14} weight="bold" className="mr-2" />
                        Pay with Razorpay · {plans[selectedPlan]?.amount_display || "—"}/month
                        <ArrowRight size={13} weight="bold" className="ml-2" />
                      </>
                    )}
                  </Button>
                  <p className="mt-3 text-xs text-ink-mute">
                    Secured by Razorpay · Test mode · No real charge in test environment.
                  </p>
                </div>
              </>
            )}
          </div>

          {/* ── Right: What's included ── */}
          <div className="xl:col-span-5">
            <div className="border border-slate-200/60 bg-white p-6 sticky top-8">
              <div className="flex items-center gap-2 mb-5">
                <Crown size={16} weight="fill" className="text-brand" />
                <span className="font-display font-bold text-lg tracking-tight">What's included</span>
              </div>

              <ul className="space-y-3">
                {PLAN_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <CheckCircle size={15} weight="fill" className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-ink leading-snug">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 pt-5 border-t border-border space-y-3">
                {[
                  { icon: Shield, text: "Cancel any time — no lock-in" },
                  { icon: Lightning, text: "Instant activation after payment" },
                  { icon: Globe, text: "Works from anywhere in the world" },
                  { icon: Sparkle, text: "Access to all future features included" },
                ].map((x) => (
                  <div key={x.text} className="flex items-center gap-2.5 text-xs text-ink-soft">
                    <x.icon size={13} weight="duotone" className="text-brand flex-shrink-0" />
                    {x.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
